/**
 * Error Handler Utility
 * Standardizes error message extraction from API responses
 */

/**
 * Type guard to check if value is a record object
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Extract error message from API error response
 * Handles multiple error formats:
 * - errors array (validation errors)
 * - message field
 * - error field
 *
 * @param error - The error object from axios/API (unknown type for flexibility)
 * @param defaultMessage - Default message if no error found
 * @returns User-friendly error message
 */
export function extractErrorMessage(
  error: unknown,
  defaultMessage = "An error occurred"
): string {
  // Type guard for error object
  if (!isRecord(error)) {
    return defaultMessage;
  }

  // Try to get error data from response
  const responseData = isRecord(error.response) ? error.response.data : undefined;
  const errorData = responseData ?? error.data;

  // Handle validation errors array (from new backend validation)
  if (isRecord(errorData) && Array.isArray(errorData.errors)) {
    const stringErrors = errorData.errors.filter(
      (e: unknown): e is string => typeof e === 'string'
    );
    if (stringErrors.length > 0) {
      return stringErrors.join(", ");
    }
  }

  // Handle single message field
  if (isRecord(errorData) && typeof errorData.message === 'string') {
    return errorData.message;
  }

  // Handle error field
  if (isRecord(errorData) && typeof errorData.error === 'string') {
    return errorData.error;
  }

  // Handle error.message (sometimes axios puts it here)
  if (typeof error.message === 'string') {
    return error.message;
  }

  // Return default
  return defaultMessage;
}

/**
 * Check if error is a specific status code
 */
export function isErrorStatus(error: unknown, statusCode: number): boolean {
  if (!isRecord(error)) {
    return false;
  }

  // Check response.status
  if (isRecord(error.response) && error.response.status === statusCode) {
    return true;
  }

  // Check direct status property
  if (error.status === statusCode) {
    return true;
  }

  return false;
}

/**
 * Check if error is a validation error (400)
 */
export function isValidationError(error: unknown): boolean {
  return isErrorStatus(error, 400);
}

/**
 * Check if error is unauthorized (401)
 */
export function isUnauthorizedError(error: unknown): boolean {
  return isErrorStatus(error, 401);
}

/**
 * Check if error is forbidden (403)
 */
export function isForbiddenError(error: unknown): boolean {
  return isErrorStatus(error, 403);
}

/**
 * Check if error is not found (404)
 */
export function isNotFoundError(error: unknown): boolean {
  return isErrorStatus(error, 404);
}

/**
 * Check if error is server error (500)
 */
export function isServerError(error: unknown): boolean {
  return isErrorStatus(error, 500);
}
